import type {
  LocalizedText,
  PermissionDefinition,
  SystemRoleDefinition,
} from '../../permissions.ts';

/** Permissões do Smart Builder: a Fábrica (equipe) e a área do cliente (ADR 0200, 0211). */
export const PERMISSION_MODULES: Record<string, LocalizedText> = {
  factory: { 'pt-BR': 'Fábrica de sistemas', en: 'System factory' },
  portal: { 'pt-BR': 'Área do cliente', en: 'Client area' },
};

export const PERMISSIONS = [
  {
    code: 'factory.system.read',
    module: 'factory',
    label: {
      'pt-BR': 'Ver os sistemas criados pela Fábrica e os servidores',
      en: 'View the systems created by the factory and the servers',
    },
  },
  {
    code: 'factory.system.manage',
    module: 'factory',
    label: {
      'pt-BR': 'Cadastrar sistemas novos e rodar as etapas de criação',
      en: 'Register new systems and run the creation steps',
    },
    sensitive: true,
  },
  {
    code: 'factory.code.ai',
    module: 'factory',
    label: {
      'pt-BR':
        'Ligar a própria IA (Claude, ChatGPT) aos sistemas: ler o código, gravar rascunhos e publicar quando a pessoa pedir',
      en: 'Connect your own AI (Claude, ChatGPT) to the systems: read the code, save drafts and publish when asked',
    },
  },
  {
    code: 'factory.server.manage',
    module: 'factory',
    label: {
      'pt-BR': 'Cadastrar e alterar os servidores de destino',
      en: 'Register and edit the target servers',
    },
    sensitive: true,
  },
  {
    // Biblioteca de módulos (ADR 0295): o anúncio e o preço voltam ao catálogo por PR.
    code: 'factory.catalog.manage',
    module: 'factory',
    label: {
      'pt-BR': 'Editar o anúncio e o preço dos módulos na Biblioteca (abre um PR no catálogo)',
      en: 'Edit module listings and prices in the Library (opens a pull request in the catalog)',
    },
    sensitive: true,
  },
  {
    code: 'factory.client.read',
    module: 'factory',
    label: {
      'pt-BR': 'Ver o painel dos clientes: todos os clientes, os sistemas e o estágio de cada um',
      en: 'View the client panel: every client, their systems and the stage of each one',
    },
  },
  {
    code: 'factory.client.manage',
    module: 'factory',
    label: {
      'pt-BR': 'Gerir os clientes: cadastrar, pausar ou liberar o acesso e ligar sistemas',
      en: 'Manage clients: register, pause or restore access and link systems',
    },
    sensitive: true,
  },
  {
    // Área do cliente (ADR 0211): dada por empresa, no perfil Cliente. Explícita para o
    // Administrador não virar "cliente" de todas as empresas.
    code: 'portal.system.read',
    module: 'portal',
    label: {
      'pt-BR': 'Área do cliente: ver os próprios sistemas e o andamento deles',
      en: 'Client area: view your own systems and their progress',
    },
    explicitOnly: true,
  },
  {
    code: 'portal.interview.manage',
    module: 'portal',
    label: {
      'pt-BR': 'Área do cliente: responder a entrevista de um sistema novo',
      en: 'Client area: answer the interview for a new system',
    },
    explicitOnly: true,
  },
  {
    code: 'portal.change.publish',
    module: 'portal',
    label: {
      'pt-BR': 'Área do cliente: revisar, publicar e voltar as mudanças dos próprios sistemas',
      en: 'Client area: review, publish and undo changes to your own systems',
    },
    explicitOnly: true,
  },
  {
    code: 'portal.ai.connect',
    module: 'portal',
    label: {
      'pt-BR':
        'Área do cliente: ligar a própria IA (Claude, ChatGPT) aos próprios sistemas, para mudar e publicar conversando',
      en: 'Client area: connect your own AI (Claude, ChatGPT) to your own systems, to change and publish by chatting',
    },
    explicitOnly: true,
  },
] as const satisfies readonly PermissionDefinition[];

/** O que o perfil Cliente recebe (ADR 0211): só a área do cliente. */
export const CLIENT_PERMISSION_CODES = [
  'portal.system.read',
  'portal.interview.manage',
  'portal.change.publish',
  'portal.ai.connect',
] as const;

/** Perfil fixo de quem é cliente (ADR 0131 do Ground Up): o seed cria e mantém. */
export const CLIENT_ROLE: SystemRoleDefinition = {
  code: 'CLIENT',
  name: { 'pt-BR': 'Cliente', en: 'Client' },
  description: {
    'pt-BR': 'Quem assinou o Smart Builder. Vê só o que é da conta dele (ADR 0279).',
    en: 'Who subscribed to Smart Builder. Sees only what belongs to their account (ADR 0279).',
  },
  permissions: CLIENT_PERMISSION_CODES,
};

export const SYSTEM_ROLES: readonly SystemRoleDefinition[] = [CLIENT_ROLE];
